let defaultDriverFactoryPromise = null;

export const loadDefaultDriverFactory = () => {
  if (!defaultDriverFactoryPromise) {
    defaultDriverFactoryPromise = Promise.all([
      import("driver.js/dist/driver.css"),
      import("driver.js"),
    ]).then(([, module]) => module.driver);
  }
  return defaultDriverFactoryPromise;
};

const defaultWaitForDom = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
      resolve();
      return;
    }
    window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
  });

const shouldReduceMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const NAV_LIFTED_CLASS = "miridar-tour-nav-lifted";
const NAV_TARGET_CLASS = "miridar-tour-nav-target";

const isElementOnScreen = (element) => {
  if (typeof element?.getBoundingClientRect !== "function") return false;
  const rect = element.getBoundingClientRect();
  if (!rect.width || !rect.height) return false;
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;
  return rect.right > 0 && rect.left < viewportWidth;
};

const clearNavHighlight = () => {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll(`.${NAV_TARGET_CLASS}`)
    .forEach((element) => element.classList.remove(NAV_TARGET_CLASS));
  document
    .querySelectorAll(`.${NAV_LIFTED_CLASS}`)
    .forEach((element) => element.classList.remove(NAV_LIFTED_CLASS));
};

/**
 * Alongside the page element, spotlight the sidebar entry the step belongs to,
 * so the user also learns where the section lives in the menu. The step route
 * is the key: nav items expose it as `data-tour-nav`.
 */
export const syncTourNavHighlight = (step) => {
  clearNavHighlight();
  if (typeof document === "undefined") return null;
  const route = String(step?.route || "").trim();
  if (!route) return null;

  const navItem = Array.from(document.querySelectorAll("[data-tour-nav]")).find(
    (element) => element.getAttribute("data-tour-nav") === route
  );
  if (!navItem) return null;

  const drawer = navItem.closest(".v-navigation-drawer");
  if (!drawer) return null;
  // A closed/off-canvas drawer (mobile) must not be raised over the overlay.
  // `offsetParent` cannot be used here: it is always null for `position: fixed`,
  // which is exactly what the drawer is, so the geometry is checked instead.
  if (!isElementOnScreen(drawer)) return null;

  navItem.classList.add(NAV_TARGET_CLASS);
  drawer.classList.add(NAV_LIFTED_CLASS);
  return navItem;
};

export const clearTourNavHighlight = clearNavHighlight;

export const findTourStepIndex = (steps, stepKey) => {
  const normalizedKey = String(stepKey || "").trim();
  if (!normalizedKey) return 0;
  const index = (Array.isArray(steps) ? steps : []).findIndex(
    (step) => String(step?.key || "").trim() === normalizedKey
  );
  return index >= 0 ? index : 0;
};

export const createTourAdapter = ({
  definition,
  router,
  labels,
  driverFactory = null,
  loadDriverFactory = loadDefaultDriverFactory,
  waitForDom = defaultWaitForDom,
  onStepChange = () => {},
  onComplete = () => {},
  onSkip = () => {},
  onResumeLater = () => {},
  onDestroyed = () => {},
}) => {
  const steps = Array.isArray(definition?.steps) ? definition.steps : [];
  let driverInstance = null;
  let driverInitialization = null;
  let terminalAction = null;
  let moving = false;

  const currentRoutePath = () => String(router?.currentRoute?.value?.path || "").trim();

  const handleDestroyed = () => {
    clearNavHighlight();
    if (!terminalAction) {
      terminalAction = "resume_later";
      const activeIndex = Number(driverInstance?.getActiveIndex?.() ?? 0);
      onResumeLater(steps[activeIndex] || steps[0] || null);
    }
    onDestroyed(terminalAction);
  };

  const prepareStep = async (step) => {
    const targetRoute = String(step?.route || "").trim();
    if (targetRoute && targetRoute !== currentRoutePath() && typeof router?.push === "function") {
      await router.push(targetRoute);
    }
    await waitForDom();
  };

  const runTerminalAction = async (action) => {
    if (terminalAction) return;
    terminalAction = action;
    const activeIndex = Number(driverInstance?.getActiveIndex?.() ?? 0);
    const activeStep = steps[activeIndex] || steps[0] || null;
    try {
      if (action === "complete") await onComplete(activeStep);
      if (action === "skip") await onSkip(activeStep);
      if (action === "resume_later") await onResumeLater(activeStep);
    } finally {
      if (typeof driverInstance?.destroy === "function") driverInstance.destroy();
      else handleDestroyed();
    }
  };

  const move = async (direction, index) => {
    if (moving || terminalAction || !driverInstance) return;
    const currentIndex = Number(index ?? driverInstance.getActiveIndex?.() ?? 0);
    const targetIndex = direction === "previous" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= steps.length) return;
    moving = true;
    try {
      await prepareStep(steps[targetIndex]);
      if (direction === "previous") driverInstance.movePrevious();
      else driverInstance.moveNext();
    } finally {
      moving = false;
    }
  };

  const addActionButton = ({ container, className, icon, label, action }) => {
    if (!container || container.querySelector(`.${className}`)) return;
    const button = document.createElement("button");
    button.type = "button";
    button.className = `driver-popover-footer-btn ${className}`;
    if (icon) {
      const iconElement = document.createElement("span");
      iconElement.className = `mdi ${icon} miridar-tour-button-icon`;
      iconElement.setAttribute("aria-hidden", "true");
      button.append(iconElement);
    }
    button.append(document.createTextNode(label));
    button.addEventListener("click", () => {
      void runTerminalAction(action);
    });
    container.append(button);
  };

  const decoratePopover = (popover, options) => {
    if (!popover?.wrapper) return;
    const index = Number(options?.index ?? driverInstance?.getActiveIndex?.() ?? 0);
    const currentStep = Math.max(1, Math.min(index + 1, steps.length));

    popover.wrapper.setAttribute("role", "dialog");
    popover.wrapper.setAttribute("aria-modal", "true");
    popover.wrapper.classList.toggle("miridar-tour-first-step", currentStep === 1);

    if (popover.title) {
      popover.title.id = "miridar-product-tour-title";
      popover.wrapper.setAttribute("aria-labelledby", popover.title.id);
    }
    if (popover.description) {
      popover.description.id = "miridar-product-tour-description";
      popover.wrapper.setAttribute("aria-describedby", popover.description.id);
    }

    let meta = popover.wrapper.querySelector(".miridar-tour-meta");
    if (!meta) {
      meta = document.createElement("div");
      meta.className = "miridar-tour-meta";

      const context = document.createElement("div");
      context.className = "miridar-tour-context";
      const icon = document.createElement("span");
      icon.className = "miridar-tour-context-icon mdi mdi-help-circle-outline";
      icon.setAttribute("aria-hidden", "true");
      const label = document.createElement("span");
      label.className = "miridar-tour-context-label";
      label.textContent = labels.guide;
      context.append(icon, label);
      meta.append(context);

      const progressTrack = document.createElement("div");
      progressTrack.className = "miridar-tour-progress-track";
      progressTrack.setAttribute("role", "progressbar");
      progressTrack.setAttribute("aria-label", labels.guide);
      progressTrack.setAttribute("aria-valuemin", "1");
      progressTrack.setAttribute("aria-valuemax", String(steps.length));
      const progressFill = document.createElement("span");
      progressFill.className = "miridar-tour-progress-fill";
      progressTrack.append(progressFill);
      meta.append(progressTrack);

      popover.wrapper.insertBefore(meta, popover.title || popover.wrapper.firstChild);
    }

    const progressTrack = meta.querySelector(".miridar-tour-progress-track");
    progressTrack?.setAttribute("aria-valuenow", String(currentStep));
    progressTrack?.style.setProperty(
      "--miridar-tour-progress",
      `${steps.length ? (currentStep / steps.length) * 100 : 0}%`
    );

    if (popover.progress) {
      popover.progress.setAttribute("aria-live", "polite");
      meta.insertBefore(popover.progress, progressTrack || null);
    }

    // `Resume later` lives in the header close button (the standard tour
    // dismiss affordance), which keeps the footer down to one secondary action
    // plus navigation so long translations do not squeeze the buttons.
    if (popover.closeButton) {
      popover.closeButton.setAttribute("aria-label", labels.resumeLater);
      popover.closeButton.setAttribute("title", labels.resumeLater);
    }

    let secondaryActions = popover.footer?.querySelector(".miridar-tour-secondary-actions");
    if (!secondaryActions && popover.footer) {
      secondaryActions = document.createElement("div");
      secondaryActions.className = "miridar-tour-secondary-actions";
      popover.footer.insertBefore(secondaryActions, popover.footerButtons || null);
    }

    addActionButton({
      container: secondaryActions,
      className: "miridar-tour-skip-button",
      label: labels.skip,
      action: "skip",
    });

    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        popover.nextButton?.focus?.({ preventScroll: true });
      });
    }
  };

  const driverSteps = steps.map((step) => ({
    element: step.element,
    waitForElement: Number(step.waitForElement || 2500),
    skipMissingElement: true,
    disableActiveInteraction: true,
    data: { key: step.key, route: step.route },
    popover: {
      title: step.title,
      description: step.description,
      side: step.side || "bottom",
      align: step.align || "center",
    },
  }));

  const initializeDriver = async () => {
    if (driverInstance) return driverInstance;
    if (!driverInitialization) {
      driverInitialization = Promise.resolve(driverFactory || loadDriverFactory())
        .then((factory) => {
          if (typeof factory !== "function") {
            throw new TypeError("Product tour driver factory is unavailable");
          }
          driverInstance = factory({
          steps: driverSteps,
          animate: !shouldReduceMotion(),
          duration: 260,
          smoothScroll: true,
          allowClose: true,
          allowScroll: true,
          allowKeyboardControl: true,
          overlayClickBehavior: "close",
          overlayColor: "rgb(var(--v-theme-primary))",
          overlayOpacity: 0.5,
          stagePadding: 12,
          stageRadius: 12,
          popoverOffset: 16,
          popoverClass: "miridar-product-tour",
          showButtons: ["previous", "next", "close"],
          showProgress: true,
          progressText: labels.progress,
          nextBtnText: labels.next,
          prevBtnText: labels.back,
          doneBtnText: labels.done,
          onNextClick: (_element, _step, options) => {
            void move("next", options?.index);
          },
          onPrevClick: (_element, _step, options) => {
            void move("previous", options?.index);
          },
          onDoneClick: () => {
            void runTerminalAction("complete");
          },
          onCloseClick: () => {
            void runTerminalAction("resume_later");
          },
          onHighlighted: (_element, _step, options) => {
            const index = Number(options?.index ?? driverInstance?.getActiveIndex?.() ?? 0);
            if (steps[index]) {
              syncTourNavHighlight(steps[index]);
              onStepChange(steps[index], index);
            }
          },
          onPopoverRender: decoratePopover,
            onDestroyed: handleDestroyed,
          });
          return driverInstance;
        })
        .catch((error) => {
          driverInitialization = null;
          throw error;
        });
    }
    return driverInitialization;
  };

  return {
    async start(stepIndex = 0) {
      const normalizedIndex = Math.max(0, Math.min(Number(stepIndex) || 0, steps.length - 1));
      await initializeDriver();
      await prepareStep(steps[normalizedIndex]);
      driverInstance.drive(normalizedIndex);
    },
    stop() {
      return runTerminalAction("resume_later");
    },
    getDriver() {
      return driverInstance;
    },
  };
};
